import { useEffect, useState } from 'react';
import * as React from 'react';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import Href from '@sdk/components/ui/Href';
import gettext from '@sdk/lang/gettext';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import useSlingApi from '@sdk/api/useSlingApi';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { IntegrationStatus, PosIntegrations } from '@sdk/integrations/types';
import type { IntegrationType } from '@sdk/integrations/types';
import type { PickerOption } from '@sdk/components/forms/types';
import { useOneClickToastConnect } from '@sdk/integrations/hooks/useOneClickToastConnect';
import { useFeatureFlag } from '@sdk/featureFlags/useFeatureFlag';
import { FeatureFlag } from '@sdk/featureFlags/types';
import config, { isProduction } from '@sdk/config/appConfig';

import getIntegrationIcon from '@web/settings/components/integrations/helpers/getIntegrationIcon';
import useConnect from '@web/settings/components/integrations/helpers/useConnect';
import IntegrationSpinner from '@web/settings/components/integrations/IntegrationSpinner';
import useCodeConnectIntegrations from '@web/settings/components/integrations/hooks/useCodeConnectIntegrations';
import IntegrationStatusLabel from '@web/settings/components/integrations/components/IntegrationStatusLabel';
import { StyledBlockButton } from '@web/settings/components/integrations/IntegrationListItemHeader.styles';

const SYNC_OPTION = 'sync';
const DISCONNECT_OPTION = 'disconnect';

type Props = {
  integration: IntegrationType;
  ConnectCodeModal: any;
  IntegrationParametersModal: any;
  refresh: () => void;
};

/**
 * This component renders first line of a integration tile.
 * It includes icon, title, short description and connect button.
 */
const IntegrationListItemHeader: React.FC<React.PropsWithChildren<Props>> = ({
  integration,
  ConnectCodeModal,
  IntegrationParametersModal,
  refresh,
}) => {
  const { type, name, description } = integration;
  const [inProgress, setInProgress] = useState(false);
  const Icon = getIntegrationIcon(type);
  const connectCodeDetails = useCodeConnectIntegrations(type);
  const { integrations } = useSlingApi();
  const { connect } = useConnect(integration);
  const { connectToast } = useOneClickToastConnect();
  const isOneClickEnabled = useFeatureFlag(FeatureFlag.TOAST_ONE_CLICK_INTEGRATION_SETUP_ENABLED);

  const optionsMenu: PickerOption[] = [
    {
      key: SYNC_OPTION,
      value: SYNC_OPTION,
      label: gettext('Sync'),
    },
    {
      key: DISCONNECT_OPTION,
      value: DISCONNECT_OPTION,
      label: gettext('Disconnect'),
    },
  ];

  const onClick = async (): Promise<void> => {
    setInProgress(true);

    // for Shopify on production, we want to redirect to Shopify Marketplace listing
    // rather than show shop slug modal; but to keep things testable on other environments,
    // we need to keep the modal around, because there's no test listing on Shopify side
    if (isProduction && integration.type === PosIntegrations.shopify) {
      window.location.href = 'https://apps.shopify.com/sling';
      return;
    }

    /**
     * Connection base on code is used for Toast integration.
     * It will open a modal with a code, user can copy it and proceed to 3rd party website.
     */
    if (type === PosIntegrations.toast && isOneClickEnabled) {
      await connectToast();
      return;
    }

    if (connectCodeDetails) {
      try {
        await ConnectCodeModal.open(integration);
        return;
      } catch {
        // Modal closed / cancelled
        setInProgress(false);
        return;
      }
    }

    /**
     * If integration expects parameters we will set them.
     * After that we still need to redirect user to integration page, the only difference is integration will now be aware of these parameters.
     */
    if (integration.parameters?.length) {
      // show modal with fields: one field for each parameter.
      try {
        const parameters = await IntegrationParametersModal.open(integration);
        // after collecting actual parameter values from user make integration side aware of them.
        await integrations.setParameters(integration.type, parameters);
      } catch {
        // Modal closed / cancelled
        setInProgress(false);
        return;
      }
    }

    await connect();
  };

  /**
   * Disconnects integration between Sling and selected platform.
   */
  const disconnect = async (): Promise<void> => {
    setInProgress(true);

    if (type === PosIntegrations.toast && isOneClickEnabled) {
      window.open(config.TOAST_CONNECT_URL, '_blank');
      return;
    }

    try {
      await integrations.disconnect(integration.type);
      refresh();
    } catch (e) {
      console.info(`error while disconnecting from ${integration.type}`, e);
    } finally {
      setInProgress(false);
    }
  };

  /**
   * Refreshes data sync between Sling and selected platform.
   */
  const sync = async (): Promise<void> => {
    setInProgress(true);
    try {
      await integrations.sync(integration.type);
    } catch (e) {
      console.info(`error while syncing with ${integration.type}`, e);
    } finally {
      setInProgress(false);
    }
  };

  const integrationOptionClicked = async (option: PickerOption): Promise<void> => {
    if (option.value === DISCONNECT_OPTION) {
      await disconnect();
    }

    if (option.value === SYNC_OPTION) {
      await sync();
    }
  };

  useEffect(() => {
    if (integration.status === IntegrationStatus.syncing) {
      setInProgress(false);
      ConnectCodeModal.close();
    }
  }, [integration.status, ConnectCodeModal]);

  return (
    <>
      <Row align-items="center" justify-content="center">
        <Row pRight={10}>
          <Icon width={32} height={32} />
        </Row>
        <Column>
          <Row>
            <Text16 semiBold themeColor={ThemeColorType.gray}>
              {name}
            </Text16>
            {integration.enabled && (
              <Row mLeft={5} alignItems="center">
                <IntegrationStatusLabel integration={integration} data-place="top" />
              </Row>
            )}
          </Row>
          <Text14 themeColor={ThemeColorType.iconGray}>{description}</Text14>
        </Column>
      </Row>
      <Row>
        {integration.enabled && !inProgress && (
          <MoreOptionsMenu options={optionsMenu} onClick={integrationOptionClicked} />
        )}
        {!integration.enabled && !inProgress && (
          <StyledBlockButton onClick={onClick} buttonType={ButtonType.default}>
            <Text14 semiBold themeColor={ThemeColorType.iconGray}>
              {gettext('Connect')}
            </Text14>
          </StyledBlockButton>
        )}
        {inProgress && <IntegrationSpinner />}
      </Row>
    </>
  );
};

export default IntegrationListItemHeader;
